import { ConfirmDialog } from "@/components/common/ConfirmDialog/ConfirmDialog"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import { IconTrash } from "@tabler/icons-react"
import { X } from "lucide-react"
import { useState } from "react"
import { toastApiError } from "../../api/apiError"
import { useRemoveArticleSubstance } from "../../api/articlesApi"

interface UnlinkArticleSubstanceButtonProps {
  articleId: string
  linkId: string
  /** `ArticleSubstanceOut.source` (article-side rows) and `ArticleRow.linkSource`
   *  (substance-side rows) are the same underlying concept under different
   *  names — see the cross-reference note on both in api/types/masterData.ts. */
  source: "declaration" | "manual" | "import"
  /** Shown in the aria-label — the substance name (article-side caller) or
   *  the article number (substance-side caller). */
  itemLabel: string
}

/**
 * Per-row unlink control shared by both directions of the substance↔article
 * link (ArticleSubstancesSection's "substances on this article" table AND
 * SubstanceArticlesSection's "articles using this substance" table). Both
 * sides read a differently-shaped row (`ArticleSubstanceOut` vs `ArticleRow`)
 * but apply the IDENTICAL business rule below, so it lives here once rather
 * than as two copies that could drift: declaration/import-sourced links warn
 * before removing, while manual links delete straight away. Callers map
 * their own row's fields onto these plain
 * props rather than this component taking either row type directly.
 */
export function UnlinkArticleSubstanceButton({
  articleId,
  linkId,
  source,
  itemLabel,
}: UnlinkArticleSubstanceButtonProps) {
  const { translate } = useTranslation()
  const { mutate, isPending } = useRemoveArticleSubstance()
  const [confirming, setConfirming] = useState(false)
  const isImport = source === "import"

  function doRemove() {
    mutate(
      { articleId, linkId },
      {
        onError: (err) => {
          toastApiError(err, translate, "MC_MD_SAVE_ERROR")
        },
      },
    )
    setConfirming(false)
  }

  return (
    <>
      <button
        type="button"
        disabled={isPending}
        aria-label={translate("MC_MD_UNLINK_LINK_ARIA", { name: itemLabel })}
        onClick={(e) => {
          e.stopPropagation()
          if (source === "declaration" || source === "import")
            setConfirming(true)
          else doRemove()
        }}
        className="text-grey5 hover:text-red1 disabled:opacity-50"
      >
        <X className="h-3.5 w-3.5" />
      </button>
      {confirming && (
        <ConfirmDialog
          title={translate(
            isImport
              ? "MC_MD_UNLINK_IMPORT_TITLE"
              : "MC_MD_UNLINK_DECLARATION_TITLE",
          )}
          message={translate(
            isImport
              ? "MC_MD_UNLINK_IMPORT_MESSAGE"
              : "MC_MD_UNLINK_DECLARATION_MESSAGE",
          )}
          confirmLabel={translate("MC_MD_UNLINK_CONFIRM_OK")}
          cancelLabel={translate("MC_MD_EDIT_CANCEL")}
          onConfirm={doRemove}
          onCancel={() => setConfirming(false)}
          icon={IconTrash}
          variant="warning"
          isLoading={isPending}
        />
      )}
    </>
  )
}
